// UI para que RRHH gestione el lote mensual de cartas médicas.
// Las solicitudes llegan en PENDIENTE_RRHH → se agregan al lote (EN_LOTE) → cerrar lote las envía a Dir Admin.

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, Alert,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';

const PRIMARY = '#0277bd';
const BR = '4px';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-AR') : '-';

export default function LoteCartaMedica({ jf, notify }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf('personal/lote-carta-medica');
            setItems(r.ok ? (r.items || []) : []);
        } catch { setItems([]); }
        setLoading(false);
    }, [jf]);

    useEffect(() => { cargar(); }, [cargar]);

    const pendientes = items.filter(s => s.Estado === 'PENDIENTE_RRHH');
    const enLote = items.filter(s => s.Estado === 'EN_LOTE');

    const agregarAlLote = async (id) => {
        setSaving(true);
        try {
            const r = await jf(`personal/solicitudes/${id}/avanzar`, { method: 'POST', body: JSON.stringify({ observacion: 'Agregada al lote de cartas médicas' }) });
            if (r.ok) { notify?.('Agregada al lote', 'success'); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setSaving(false);
    };

    const cerrarLote = async () => {
        if (enLote.length === 0) { notify?.('No hay cartas en el lote', 'warning'); return; }
        setSaving(true);
        try {
            const r = await jf('personal/lote-carta-medica/cerrar', { method: 'POST' });
            if (r.ok) { notify?.(`Lote cerrado — ${r.avanzadas} cartas enviadas a Dirección Administrativa`, 'success'); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setSaving(false);
    };

    const FilaSolicitud = ({ s, accion, labelAccion, colorAccion }) => (
        <TableRow hover>
            <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{s.Apellido}, {s.Nombre}</TableCell>
            <TableCell sx={{ fontSize: '0.78rem', color: '#666' }}>{s.Area_Empleado || '—'}</TableCell>
            <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(s.Fecha_Desde)}</TableCell>
            <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(s.Fecha_Hasta)}</TableCell>
            <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>{s.Dias_Habiles}</TableCell>
            <TableCell sx={{ fontSize: '0.78rem' }}>{fmtDate(s.Fecha_Solicitud)}</TableCell>
            <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                {accion && (
                    <Button size="small" variant="outlined" startIcon={accion.icon} disabled={saving}
                        onClick={() => accion.handler(s.Id)}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', borderColor: colorAccion, color: colorAccion, borderRadius: BR }}>
                        {labelAccion}
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#bf360c', flex: 1 }}>
                    Lote de Cartas Médicas
                </Typography>
                <Button size="small" startIcon={<RefreshIcon />} onClick={cargar} sx={{ textTransform: 'none' }}>Refrescar</Button>
            </Box>

            {/* Pendientes para agregar al lote */}
            {pendientes.length > 0 && (
                <Paper sx={{ mb: 2, borderRadius: BR, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: '#fff3e0', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon sx={{ color: '#e65100', fontSize: 18 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100' }}>
                            Nuevas solicitudes ({pendientes.length}) — esperando agregar al lote
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead><TableRow>
                                {['Empleado', 'Área', 'Desde', 'Hasta', 'Días', 'Solicitud', 'Acción'].map(h =>
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{h}</TableCell>
                                )}
                            </TableRow></TableHead>
                            <TableBody>
                                {pendientes.map(s => (
                                    <FilaSolicitud key={s.Id} s={s}
                                        accion={{ icon: <AddIcon />, handler: agregarAlLote }}
                                        labelAccion="Agregar al lote" colorAccion="#e65100" />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Lote actual */}
            <Paper sx={{ borderRadius: BR, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#e8f5e9', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32', flex: 1 }}>
                        Lote actual ({enLote.length} cartas)
                    </Typography>
                    {enLote.length > 0 && (
                        <Button variant="contained" size="small" startIcon={<SendIcon />} onClick={cerrarLote} disabled={saving}
                            sx={{ bgcolor: '#2e7d32', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#1b5e20' } }}>
                            {saving ? 'Enviando...' : `Cerrar lote y derivar (${enLote.length})`}
                        </Button>
                    )}
                </Box>
                {enLote.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                        <Alert severity="info" sx={{ borderRadius: BR }}>
                            El lote está vacío. Agregá cartas médicas desde la lista de pendientes.
                        </Alert>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead><TableRow>
                                {['Empleado', 'Área', 'Desde', 'Hasta', 'Días', 'Solicitud', ''].map(h =>
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{h}</TableCell>
                                )}
                            </TableRow></TableHead>
                            <TableBody>
                                {enLote.map(s => <FilaSolicitud key={s.Id} s={s} />)}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {pendientes.length === 0 && enLote.length === 0 && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: BR }}>No hay cartas médicas pendientes ni en lote.</Alert>
            )}
        </Box>
    );
}
